import React from "react";
import {
  TLBaseShape,
  RecordProps,
  T,
  Rectangle2d,
  HTMLContainer,
  BaseBoxShapeUtil,
  Editor,
  TLResizeInfo,
} from "@tldraw/tldraw";
import { getDashArray } from "../new-geo-shape-helpers";
import { HOVER_EFFECT } from "./constants";
import { mixColors } from "./CustomCloudUtil";
import { ShapeUtilHelper } from "./ShapeUtilHelper";
import { defaultScale } from "../constants";

type CustomEllipseUtilType = TLBaseShape<
  "customEllipse",
  {
    w: number;
    h: number;
    fill: string;
    outlineColor: string;
    dash: "solid" | "dashed" | "dotted" | "dashed solid" | "custom";
    transparency: number;
    strokeDasharray?: string;
    strokeWidth?: number;
    strokeAlign?: "inside" | "center" | "outside";
    filterBlur?: number;
    isCreatedFilterBlur?: boolean;
    filterBlurValueWhenHidden?: number;
    borderWidth?: number;
    borderColor?: string;
    borderSolid?: "solid" | "dashed";
    borderTopStyle?: string;
    borderBottomStyle?: string;
    borderLeftStyle?: string;
    borderRightStyle?: string;
    isShowBorderTop?: boolean;
    isShowBorderBottom?: boolean;
    isShowBorderLeft?: boolean;
    isShowBorderRight?: boolean;
    outShadowArray?: {
      [key: string]: {
        id: string;
        dx: number;
        dy: number;
        shadowBlur: number;
        color: string;
        opacity: number;
        isHidden: boolean;
      };
    };
    inShadowArray?: {
      [key: string]: {
        id: string;
        dx: number;
        dy: number;
        shadowBlur: number;
        color: string;
        opacity: number;
        isHidden: boolean;
      };
    };
    previousBorderColor?: string;
    isCreatedBorderItem?: boolean;
    colorMix: {
      [key: string]: {
        color: string;
        isHidden: boolean;
      };
    };
    outlineColorMix: {
      [key: string]: {
        color: string;
        isHidden: boolean;
      };
    };
    isHiddenLayerBlur?: boolean;
    isHiddenBorder?: boolean;
    isShowIndicator?: boolean;
    isScale?: boolean;
    scale?: number;
    [key: string]: any;
  }
>;

export class CustomEllipseUtil extends BaseBoxShapeUtil<CustomEllipseUtilType> {
  private shapeUtilHelper: ShapeUtilHelper;
  constructor(editor: Editor) {
    super(editor);
    this.shapeUtilHelper = new ShapeUtilHelper(editor);
  }

  static override type = "customEllipse" as const;
  static override props: any;

  // Default properties for the shape
  getDefaultProps(): CustomEllipseUtilType["props"] {
    return {
      w: 100,
      h: 100,
      fill: "#e3e3e3",
      outlineColor: "#000000",
      dash: "solid",
      transparency: 1,
      strokeWidth: 0,
      strokeAlign: "inside",
      filterBlur: 0,
      isCreatedFilterBlur: false,
      filterBlurValueWhenHidden: 0,
      borderWidth: 0,
      borderColor: "#000000",
      borderSolid: "solid",
      borderTopStyle: "solid",
      borderBottomStyle: "solid",
      borderLeftStyle: "solid",
      borderRightStyle: "solid",
      isShowBorderTop: true,
      isShowBorderBottom: true,
      isShowBorderLeft: true,
      isShowBorderRight: true,
      outShadowArray: {},
      inShadowArray: {},
      previousBorderColor: "#000000",
      isCreatedBorderItem: false,
      colorMix: {
        "0": { color: "#e3e3e3", isHidden: false },
      },
      outlineColorMix: {},
      isHiddenLayerBlur: false,
      isHiddenBorder: false,
      isShowIndicator: true,
      isScale: false,
      scale: defaultScale,
    };
  }

  getGeometry(shape: CustomEllipseUtilType) {
    return this.shapeUtilHelper.calculateAdjustedDimensions(shape);
  }
  hideSelectionBoundsFg(_shape: CustomEllipseUtilType): boolean {
    return false; // outline visibility is controlled by isChangingStyle in useHideSelectionOnChange
  }
  hideSelectionBoundsBg(shape: CustomEllipseUtilType): boolean {
    return false; // Always show selection bounds regardless of opacity
  }
  hideResizeHandles(shape: CustomEllipseUtilType): boolean {
    return false; // Always show resize handles regardless of opacity
  }
  isAspectRatioLocked(shape: CustomEllipseUtilType): boolean {
    return !!shape?.props?.isScale;
  }
  onResize(shape: CustomEllipseUtilType, info: TLResizeInfo<CustomEllipseUtilType>) {
    if (shape?.props?.isScale) {
      return this.shapeUtilHelper.resizeScaled(shape, info);
    } else {
      return this.shapeUtilHelper.resizeDefault(shape, info);
    }
  }

  component(shape: CustomEllipseUtilType) {
    const props = this.shapeUtilHelper.getSafeProps(shape);
    const { w, h, outlineColor, dash, strokeDasharray, outShadowArray, inShadowArray } = props;

    const shadowFilterId = `shadow-${shape.id}`; // Unique ID for the shadow filter
    const innerShadowFilterId = `inner-shadow-${shape.id}`; // Unique ID for the inner shadow filter
    const dashArray = strokeDasharray || getDashArray(dash);

    // Example implementations for the path functions
    function getEllipsePath(w: number, h: number): string {
      const rx = w / 2;
      const ry = h / 2;
      return `M ${rx}, 0 A ${rx}, ${ry} 0 1,0 ${rx}, ${h} A ${rx}, ${ry} 0 1,0 ${rx}, 0`;
    }

    function getDrawEllipsePath(w: number, h: number, strokeWidth: number, id: string): string {
      // Implementation for a drawn (dashed) ellipse path
      return getEllipsePath(w, h);
    }

    const pathData = dash === "solid" ? getEllipsePath(w, h) : getDrawEllipsePath(w, h, 2, shape.id);

    return this.shapeUtilHelper.useRenderedShape(
      shape,
      pathData,
      outlineColor,
      dashArray,
      shadowFilterId,
      outShadowArray,
      inShadowArray,
      this.indicator.bind(this)
    );
  }

  addDefaultColorMix(shape: CustomEllipseUtilType) {
    this.shapeUtilHelper.addDefaultColorMix(shape);
  }
  addDefaultOutShadow(shape: CustomEllipseUtilType) {
    this.shapeUtilHelper.addDefaultOutShadow(shape);
  }
  addDefaultInShadow(shape: CustomEllipseUtilType) {
    this.shapeUtilHelper.addDefaultInShadow(shape);
  }

  // Indicator function to display shape in editor
  indicator(shape: CustomEllipseUtilType) {
    const {
      h,
      w,
      dash,
      scale,
      borderWidth,
      isShowBorderBottom,
      isShowBorderTop,
      isShowBorderLeft,
      isShowBorderRight,
      isShowIndicator,
      isHiddenBorder,
    } = shape.props;
    const adjustedHeight =
      h +
      (isShowBorderTop && !isHiddenBorder ? borderWidth ?? 0 : 0) +
      (isShowBorderBottom && !isHiddenBorder ? borderWidth ?? 0 : 0);
    const adjustedWidth =
      w +
      (isShowBorderLeft && !isHiddenBorder ? borderWidth ?? 0 : 0) +
      (isShowBorderRight && !isHiddenBorder ? borderWidth ?? 0 : 0);

    const pathData = dash === "solid" ? getEllipsePath(w, h) : getDrawEllipsePath(w, h, 2, shape.id);

    // ALWAYS render indicator, ignore isShowIndicator prop
    return (
      <path
        d={pathData}
        transform={`translate(${isShowBorderLeft && !isHiddenBorder ? borderWidth ?? 0 : 0}, ${
          isShowBorderTop && !isHiddenBorder ? borderWidth ?? 0 : 0
        })`}
        stroke="#338bff"
        strokeWidth="2"
        fill="none"
        opacity="1"
        style={{
          stroke: "#338bff",
          strokeWidth: 2,
          fill: "none",
          opacity: 1,
        }}
      />
    );
  }
}

function getEllipsePath(w: number, h: number): string {
  const rx = w / 2;
  const ry = h / 2;
  return `M ${rx}, 0 A ${rx}, ${ry} 0 1,0 ${rx}, ${h} A ${rx}, ${ry} 0 1,0 ${rx}, 0`;
}

function getDrawEllipsePath(w: number, h: number, strokeWidth: number, id: string): string {
  return getEllipsePath(w, h);
}
